import * as RoleTypes from 'access/modules/RoleModule/RoleModule.types';
import moment from 'moment';
import { OpenStackApiClient } from 'openstack-api-client';
import * as R from 'ramda';
import router from 'system/router';
import { CryptoHelper } from 'utils/CryptoHelper';
import { ReduxHelper } from 'utils/ReduxHelper';
import { ReduxTypeUtils } from 'utils/ReduxTypeUtils';
import { Selector } from 'utils/ReduxTypeUtils/ReduxTypeUtils';

import { GlobalState } from 'reactApp/core/rootReducer';
import {
    CurrentUserModule,
    CurrentUserModuleState,
} from 'reactApp/modules/CurrentUserModule/CurrentUserModule';
import * as CurrentUserTypes from 'reactApp/modules/CurrentUserModule/CurrentUserModule.types';

const getModuleState = CurrentUserModule.getModuleState;

function getAllProjects(state: GlobalState) {
    return getModuleState(state).projects || [];
}

function getAllEnabledProjects(state: GlobalState) {
    return getAllProjects(state).filter((project) => project.enabled);
}

const getPartnerProjects = R.pipe(
    getAllEnabledProjects,
    R.filter(R.propEq('is_partner', true)),
) as ReduxTypeUtils.Selector<CurrentUserTypes.ProjectShortData[]>;

const getRegularProjects = R.pipe(
    getAllEnabledProjects,
    R.filter(R.propEq('is_partner', false)),
) as ReduxTypeUtils.Selector<CurrentUserTypes.ProjectShortData[]>;

const getCurrentAppProjects: ReduxTypeUtils.Selector<CurrentUserTypes.ProjectShortData[]> = (
    state,
) => {
    if (router.isPartnerApp()) {
        return getPartnerProjects(state);
    }
    return getRegularProjects(state);
};

const getUid = R.pipe(getModuleState, (s) => s.uid);

const getPid = (state: GlobalState) => getModuleState(state).selectedPid;

function getHasEnabledProjectForSelectedPid(state: GlobalState) {
    const pid = getPid(state);
    const projects = getAllEnabledProjects(state);
    return projects.some((project) => project.pid === pid && project.enabled);
}

const getProject = (state: GlobalState, pid: string) =>
    getAllEnabledProjects(state).find(R.propEq('pid', pid));

const getIsPartner = (state: GlobalState, pid = getPid(state)) => {
    const project = getProject(state, pid);

    return project && project.is_partner;
};

const getCurrentProject: ReduxTypeUtils.Selector<
    CurrentUserTypes.ProjectShortData | undefined
> = R.converge(
    (projects: CurrentUserTypes.ProjectShortData[], pid: string) =>
        projects.find(R.propEq('pid', pid)),
    [getCurrentAppProjects, getPid],
);

const getRoles: ReduxTypeUtils.Selector<RoleTypes.Role[]> = R.pipe(
    getCurrentProject,
    (project) => (project ? project.roles : []),
);

const getIsFederativeProject = R.pipe(getCurrentProject, (project) =>
    Boolean(project?.fed_exists),
);
const getIsFederativeUser = R.pipe(
    getModuleState,
    (data) => data.attr?.type === CurrentUserTypes.UserTypeEnum.fed,
);
const getIsNotFedUser = R.pipe(
    getModuleState,
    (data) => data.attr?.type === CurrentUserTypes.UserTypeEnum.mcs,
);

function getIsTfaEnabled(state) {
    return CurrentUserModule.getModuleState(state).tfa;
}

function getIsTfaNotEnabled(state: GlobalState) {
    return !getIsTfaEnabled(state);
}

function getIsTfaRequired(state: GlobalState) {
    const project = getCurrentProject(state);
    return project?.tfa?.enabled && getIsNotFedUser(state);
}

function getMustEnableTfa(state: GlobalState) {
    return getIsTfaRequired(state) && getIsTfaNotEnabled(state);
}

const getHasPartnerProject = R.pipe(getPartnerProjects, R.length, R.lt(0));

const getHasAnyProject = R.pipe(getAllEnabledProjects, R.length, Boolean);

const getVerifiedObj = R.pipe(
    getModuleState,
    R.propOr(undefined, 'verified'),
) as Selector<CurrentUserTypes.VerifiedObj | undefined>;

const getIsEmailVerified = R.pipe(
    getVerifiedObj,
    R.propOr(false, 'email'),
) as Selector<boolean>;

const getIsPhoneVerified = R.pipe(
    getVerifiedObj,
    R.propOr(false, 'phone'),
) as Selector<boolean>;

const getEmail = (state: GlobalState) => getModuleState(state).email;

const getPhone = R.pipe(getModuleState, (state) => state.phone);
const getDomain = R.pipe(getModuleState, (state) => state.domain);

const getEmailUID = R.pipe(
    getEmail,
    (email) => CryptoHelper.getInt36Fnv32aHash(email || '') as string,
);

const getContactInfo = R.pipe(
    getModuleState,
    ({
        email,
        name,
        phone,
        attr: { company = '', position = '' } = {
            type: CurrentUserTypes.UserTypeEnum.mcs,
        },
    }: CurrentUserModuleState) => ({ email, name, phone, company, position }),
);

const getIsUserEnabled = R.pipe(getModuleState, R.propOr(false, 'enabled'));

const getIsUserDisabled = R.pipe(getIsUserEnabled, R.not);

const getInfraToken = () => {
    return OpenStackApiClient.getInfraToken()?.token || null;
};

const getCanEarnBonuses = (state) => {
    const currentProject = getProject(state, getPid(state));

    return currentProject && !currentProject.skip_mcs_bonus;
};

function getProjectsOSData(state: GlobalState) {
    return getModuleState(state).projectsOSData;
}

function getUserProjectsOSIds(state: GlobalState) {
    return Object.values(getProjectsOSData(state)).map(
        (projectOS) => projectOS.os_project_id,
    );
}

function getUserProjectOSId(state: GlobalState) {
    const pid = CurrentUserSelectors.getPid(state);
    return getProjectsOSData(state)[pid]?.os_project_id;
}

const getProjectByProjectOSId = ReduxHelper.createSelectorWithArguments(
    [getProjectsOSData, getAllProjects],
    (osData, allProjects, id) => {
        const projectPid = Object.keys(osData).find(
            (key) => osData[key].os_project_id === id,
        );

        return allProjects.find(R.propEq('pid', projectPid));
    },
);

const getIsFirstLoginAfterEmailConfirmation = (state) => {
    return getModuleState(state).isFirstLoginAfterEmailConfirmation;
};
const getIsShowPhoneVerificationDialog = (state) => {
    return getModuleState(state).isShowPhoneVerificationDialog;
};

const getIsAdminOfNetwork = (state) => {
    const roles = CurrentUserSelectors.getRoles(state);

    return roles.length === 1 && roles.includes('mcs_admin_network');
};

const getSignupMoment = (state: GlobalState) => {
    const { ctime } = getModuleState(state);
    return moment((ctime || 0) * 1000);
};

const getIsNewUser = (state: GlobalState) => {
    const { ctime } = getModuleState(state);
    return ctime && new Date(ctime * 1000) > new Date('2020-09-28');
};

export const CurrentUserSelectors = {
    getIsNewUser,
    getProjectsOSData,
    getIsAdminOfNetwork,
    getIsFirstLoginAfterEmailConfirmation,
    getIsShowPhoneVerificationDialog,
    getModuleState,
    getEmail,
    getPhone,
    getIsVerified: getIsEmailVerified,
    getIsPhoneVerified,
    getHasPartnerProject,
    getHasAnyProject,
    getEmailUID,
    getMustEnableTfa,
    getPid,
    getDomain,
    getUid,
    getRoles,
    getAllEnabledProjects,
    getPartnerProjects,
    getRegularProjects,
    getCurrentAppProjects,
    getProject,
    getCurrentProject,
    getIsTfaEnabled,
    getIsTfaNotEnabled,
    getIsTfaRequired,
    getContactInfo,
    getIsUserEnabled,
    getIsUserDisabled,
    getInfraToken,
    getCanEarnBonuses,
    getHasEnabledProjectForSelectedPid,
    getVerifiedObj,
    getIsPartner,
    getUserProjectsOSIds,
    getUserProjectOSId,
    getProjectByProjectOSId,
    getIsFederativeProject,
    getIsFederativeUser,
    getIsNotFedUser,
    getSignupMoment,
};

export const BoundCurrentUserSelectors = ReduxHelper.bindSelectors<
    typeof CurrentUserSelectors
>(CurrentUserSelectors);
